import {
  ActiveRequestsPage,
  BillingPage,
  BillingPortalPage,
  MorePage,
  PastRequestsPage,
  RequestASubPage,
  RequestDetails,
  SchoolPage,
  SchoolsPage,
  SubMapTool,
  SubProfilePage,
} from "pages";
import { RequestsHeader } from "pages/RequestIndex";
import { WaitlistPage } from "pages/waitlist/Waitlist.page";
import { FaSchool } from "react-icons/fa";
import { IconType } from "react-icons/lib";
import {
  MdHistory,
  MdMonetizationOn,
  MdMoreHoriz,
  MdPersonAddAlt1,
  MdViewList,
} from "react-icons/md";
import { generatePath } from "react-router-dom";
import { COMBINED_RESTRICTIONS } from "utils/security-restrictions";
// FIXME: We should not use `translate` in this file since this file is being
// imported to many components which may bring import this utility again.
import { translate } from "utils/string-table";

export type sideMenuItemProps = {
  text: string;
  logo: IconType;
  linkUrl: string;
  testId: string;
  hiddenFor?: string[] | string[][];
};

export const SIDE_MENU_TEST_ID_PREFIX = "side_menu_items_top";

export const sideMenuItemsTop: sideMenuItemProps[] = [
  {
    text: translate("SIDE_MENU_LABEL_ACTIVE_REQUESTS"),
    logo: MdViewList,
    linkUrl: "/active-requests",
    testId: `${SIDE_MENU_TEST_ID_PREFIX}-active-requests-button`,
  },
  {
    text: translate("SIDE_MENU_LABEL_PAST_REQUESTS"),
    logo: MdHistory,
    linkUrl: "/past-requests",
    testId: `${SIDE_MENU_TEST_ID_PREFIX}-pasts_requests_button`,
  },
  {
    text: translate("SIDE_MENU_LABEL_REQUEST_A_SUB"),
    logo: MdPersonAddAlt1,
    linkUrl: "/request-a-sub",
    hiddenFor: [COMBINED_RESTRICTIONS.EDIT_REQUESTS],
    testId: `${SIDE_MENU_TEST_ID_PREFIX}-request_a_sub_button`,
  },
  {
    text: translate("SIDE_MENU_LABEL_SCHOOLS"),
    logo: FaSchool,
    linkUrl: "/schools",
    testId: `${SIDE_MENU_TEST_ID_PREFIX}-schools_button`,
  },
  {
    text: translate("SIDE_MENU_LABEL_BILLING"),
    logo: MdMonetizationOn,
    linkUrl: "/billing-portal",
    hiddenFor: [COMBINED_RESTRICTIONS.CLASSROOM_ADMIN],
    testId: `${SIDE_MENU_TEST_ID_PREFIX}-billing-button`,
  },
];

export type mobileNavItemProps = {
  text: string;
  tab: string;
  logo: IconType;
  hiddenFor?: string[] | string[][];
};

export const mobileNavItems: mobileNavItemProps[] = [
  {
    text: "Active",
    tab: "active-requests",
    logo: MdViewList,
  },
  {
    text: "Past",
    tab: "past-requests",
    logo: MdHistory,
  },
  {
    text: "Request",
    tab: "request-a-sub",
    logo: MdPersonAddAlt1,
    hiddenFor: [COMBINED_RESTRICTIONS.EDIT_REQUESTS],
  },
  {
    text: "More",
    tab: "more",
    logo: MdMoreHoriz,
  },
];

/** SEGMENTS **/
export const SEGMENTS = {
  activeRequests: "active-requests",
  pastRequests: "past-requests",
  subs: "subs",
  requestASub: "request-a-sub",
};

/** PATHS **/
export const PATHS = {
  requests: `/:requestType(${SEGMENTS.activeRequests}|${SEGMENTS.pastRequests})`,
  requestDetails: `/:requestType(${SEGMENTS.activeRequests}|${SEGMENTS.pastRequests})/:requestId`,
  subProfile: `/:requestType(${SEGMENTS.activeRequests}|${SEGMENTS.pastRequests})/${SEGMENTS.subs}/:subId`,
  requestASub: `/${SEGMENTS.requestASub}`,
  more: "/more",
  billing: `/more/billing`,
  billingPortal: `/billing-portal`,
  schools: "/schools",
  school: "/schools/:schoolId",
  subMap: "/subMap",
  subMapProfile: "/subMap/sub/:subId",
  waitlist: "/waitlist",
};

/** Path Helpers **/
export function createRequestsPath(requestType: "active-requests" | "past-requests") {
  return generatePath(PATHS.requests, { requestType });
}

export function createRequestDetailsPath(
  requestType: "active-requests" | "past-requests",
  requestId: string,
) {
  return generatePath(PATHS.requestDetails, { requestType, requestId });
}

export function createSubProfilePath(
  requestType: "active-requests" | "past-requests",
  subId: string,
) {
  return generatePath(PATHS.subProfile, { requestType, subId });
}

export function createRequestASubPath() {
  return PATHS.requestASub;
}

export function createMorePath() {
  return PATHS.more;
}

export function createBillingPath() {
  return PATHS.billing;
}

export function createBillingPortalPath() {
  return PATHS.billingPortal;
}

export function createSchoolIndexPath() {
  return PATHS.schools;
}

export function createSchoolPath(schoolId: string) {
  return generatePath(PATHS.school, { schoolId });
}

// SubMap Paths
function createSubMapPath() {
  return PATHS.subMap;
}

export function createSubMapProfilePath(subId: string) {
  return generatePath(PATHS.subMapProfile, { subId });
}

function createWaitlistPath() {
  return PATHS.waitlist;
}

/** Routes Table **/
export const routeTable: Array<{
  desktopTitle?: string | (() => JSX.Element); // When undefined, the page will not have a title.
  mobileTitle?: string | (() => JSX.Element); // When undefined, the page will not have a title.
  path: string;
  component: () => JSX.Element;
}> = [
  // Requests Page
  {
    desktopTitle: () =>
      RequestsHeader({
        requestType: "active-requests",
      }),
    mobileTitle: () =>
      RequestsHeader({
        requestType: "active-requests",
      }),
    path: createRequestsPath("active-requests"),
    component: ActiveRequestsPage,
  },
  {
    desktopTitle: () =>
      RequestsHeader({
        requestType: "past-requests",
      }),
    mobileTitle: () =>
      RequestsHeader({
        requestType: "past-requests",
      }),
    path: createRequestsPath("past-requests"),
    component: PastRequestsPage,
  },
  // Request Details Page
  {
    mobileTitle: translate("ROUTE_TABLE_REQUEST_DETAILS"),
    path: createRequestDetailsPath("active-requests", ":requestId"),
    component: RequestDetails,
  },
  {
    mobileTitle: translate("ROUTE_TABLE_REQUEST_DETAILS"),
    path: createRequestDetailsPath("past-requests", ":requestId"),
    component: RequestDetails,
  },
  // Sub Profile Page
  {
    mobileTitle: translate("ROUTE_TABLE_SUB_PROFILE"),
    path: createSubProfilePath("active-requests", ":subId"),
    component: SubProfilePage,
  },
  {
    mobileTitle: translate("ROUTE_TABLE_SUB_PROFILE"),
    path: createSubProfilePath("past-requests", ":subId"),
    component: SubProfilePage,
  },
  // Request A Sub
  {
    desktopTitle: translate("ROUTE_TABLE_REQUEST_SUB"),
    mobileTitle: translate("ROUTE_TABLE_REQUEST_SUB"),
    path: createRequestASubPath(),
    component: RequestASubPage,
  },
  // More
  {
    desktopTitle: translate("ROUTE_TABLE_MORE"),
    mobileTitle: translate("ROUTE_TABLE_MORE"),
    path: createMorePath(),
    component: MorePage,
  },
  // Schools Page
  {
    desktopTitle: translate("ROUTE_TABLE_SCHOOLS"),
    mobileTitle: translate("ROUTE_TABLE_SCHOOLS"),
    path: createSchoolIndexPath(),
    component: SchoolsPage,
  },
  // School Page
  {
    mobileTitle: translate("ROUTE_TABLE_SCHOOL"),
    path: createSchoolPath(":schoolId"),
    component: SchoolPage,
  },
  // Billing Page
  {
    mobileTitle: translate("ROUTE_TABLE_BILLING_PAGE"),
    desktopTitle: translate("ROUTE_TABLE_BILLING_PAGE"),
    path: createBillingPath(),
    component: BillingPage,
  },
  // Billing Portal
  {
    mobileTitle: translate("SIDE_MENU_LABEL_BILLING"),
    desktopTitle: translate("SIDE_MENU_LABEL_BILLING"),
    path: createBillingPortalPath(),
    component: BillingPortalPage,
  },
  // Sub Map
  {
    mobileTitle: translate("WAITLIST"),
    path: createSubMapPath(),
    component: SubMapTool,
  },
  // Sub Map Sub Info
  {
    mobileTitle: translate("WAITLIST"),
    path: createSubMapProfilePath(":subId"),
    component: SubProfilePage,
  },

  // Sub Map
  {
    mobileTitle: translate("WAITLIST"),
    path: createWaitlistPath(),
    component: WaitlistPage,
  },
];

/***** Helpers ******/
// Move the object into the routeTable instead of pushing
// it in like this here once we release Schools Index. I
// believe this is the most graceful way of handling things.
export function isActiveRequestsRoute(routeHref: string) {
  return routeHref.includes(SEGMENTS.activeRequests);
}

export function isPastRequestsRoute(routeHref: string) {
  return routeHref.includes(SEGMENTS.pastRequests);
}
